import { Link } from 'react-router-dom'
import { query } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  ClientFundsControl,
  IntakeAgeBucket,
  Paged,
  JournalSummary,
  ProfitAndLoss,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import {
  Empty,
  ErrorBanner,
  Loading,
  Money,
  Panel,
  endOfMonth,
  formatDate,
  isoDate,
  money,
  startOfMonth,
} from '../components/ui'

/**
 * The two things worth seeing every morning: whether client money is intact, and what the month
 * looks like on both bases. Deliberately not a wall of charts — BUILD_PLAN §10 puts dashboards
 * explicitly on the "not on this list" list.
 */
export function DashboardPage() {
  const { can, user } = useAuth()
  const today = isoDate(new Date())
  const from = startOfMonth()
  const to = endOfMonth()

  const funds = useQuery<ClientFundsControl>(
    can(P.reports.clientFunds) ? `/reports/client-funds${query({ asOf: today })}` : null,
  )

  const pl = useQuery<ProfitAndLoss>(
    can(P.reports.view) ? `/reports/profit-and-loss${query({ from, to })}` : null,
  )

  const intake = useQuery<IntakeAgeBucket[]>(can(P.documents.view) ? '/documents/ageing' : null)

  const drafts = useQuery<Paged<JournalSummary>>(
    can(P.journal.view) ? `/journal${query({ status: 'Draft', size: 8 })}` : null,
  )

  return (
    <>
      <div className="banner info">
        Good to see you, <strong>{user?.fullNameEn}</strong>. Showing{' '}
        {formatDate(from)} – {formatDate(to)}.
      </div>

      <ErrorBanner error={funds.error ?? pl.error} />

      {/* The client funds control check. BUILD_PLAN §10.2 calls this the highest-value report in
          the system, so it gets the top of the screen and its own colour. */}
      {can(P.reports.clientFunds) && (
        <>
          {funds.loading && <Loading what="Checking client funds" />}

          {funds.data && (
            <div className="grid cols-4" style={{ marginBottom: 16 }}>
              <div className={funds.data.isHealthy ? 'metric good' : 'metric alert'}>
                <div className="label">Client funds check</div>
                <div className="value">{money(funds.data.variance)}</div>
                <div className="hint">
                  {funds.data.isHealthy
                    ? 'Cash held covers the obligation to clients.'
                    : 'Client money is short. Investigate before anything else.'}
                </div>
              </div>

              <div className="metric">
                <div className="label">Custody held (3ohda)</div>
                <div className="value">{money(funds.data.totalCustodyHeld)}</div>
                <div className="hint">A liability, not revenue.</div>
              </div>

              <div className="metric">
                <div className="label">Settlements held</div>
                <div className="value">{money(funds.data.totalSettlementsHeld)}</div>
                <div className="hint">Collected for clients, owed out.</div>
              </div>

              <div className="metric">
                <div className="label">Client cash actually held</div>
                <div className="value">{money(funds.data.totalClientCash)}</div>
                <div className="hint">
                  <Link to="/reports/client-funds">Open the full report →</Link>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="grid cols-2">
        {can(P.reports.view) && (
          <Panel title="This month — cash vs accrual" flush>
            {pl.loading && <Loading />}
            {pl.data && (
              <table>
                <thead>
                  <tr>
                    <th />
                    <th className="num">Cash</th>
                    <th className="num">Accrual</th>
                    <th className="num">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Revenue</td>
                    <td className="num">
                      <Money value={pl.data.totalRevenue.cash} />
                    </td>
                    <td className="num">
                      <Money value={pl.data.totalRevenue.accrual} />
                    </td>
                    <td className="num">
                      <Money value={pl.data.totalRevenue.difference} />
                    </td>
                  </tr>
                  <tr>
                    <td>Expenses</td>
                    <td className="num">
                      <Money value={pl.data.totalExpenses.cash} />
                    </td>
                    <td className="num">
                      <Money value={pl.data.totalExpenses.accrual} />
                    </td>
                    <td className="num">
                      <Money value={pl.data.totalExpenses.difference} />
                    </td>
                  </tr>
                  <tr className="subtotal">
                    <td>Net result</td>
                    <td className="num">
                      <Money value={pl.data.netResult.cash} />
                    </td>
                    <td className="num">
                      <Money value={pl.data.netResult.accrual} />
                    </td>
                    <td className="num">
                      <Money value={pl.data.netResult.difference} />
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            {pl.data && !pl.data.reconciliation.holds && (
              <div className="banner warn" style={{ margin: 12 }}>
                The two bases do not reconcile — {money(pl.data.reconciliation.unexplained)}{' '}
                unexplained. A cash movement carries the wrong basis flag.
              </div>
            )}

            <div style={{ padding: '10px 14px' }}>
              <Link to="/reports/profit-and-loss">Open the full P&amp;L →</Link>
            </div>
          </Panel>
        )}

        {can(P.documents.view) && (
          <Panel title="Document intake — not yet in the ledger" flush>
            {intake.loading && <Loading />}
            {intake.data?.length === 0 && <Empty>Nothing waiting. The register is clear.</Empty>}

            {!!intake.data?.length && (
              <table>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th className="num">Count</th>
                    <th className="num">Value</th>
                    <th className="num">Oldest</th>
                  </tr>
                </thead>
                <tbody>
                  {intake.data.map((bucket) => (
                    <tr key={bucket.status}>
                      <td>{bucket.status}</td>
                      <td className="num">{bucket.count}</td>
                      <td className="num">
                        <Money value={bucket.total} />
                      </td>
                      <td className={bucket.oldestDays > 30 ? 'num negative' : 'num'}>
                        {bucket.oldestDays} days
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ padding: '10px 14px' }}>
              <Link to="/documents">Open the intake register →</Link>
            </div>
          </Panel>
        )}
      </div>

      {can(P.journal.view) && (
        <Panel title="Drafts waiting to be posted" flush>
          {drafts.loading && <Loading />}
          {drafts.data?.items.length === 0 && <Empty>No drafts. Everything is posted.</Empty>}

          {!!drafts.data?.items.length && (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Date</th>
                  <th style={{ width: 90 }}>Book</th>
                  <th>Description</th>
                  <th style={{ width: 100 }}>Basis</th>
                  <th style={{ width: 130 }} className="num">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {drafts.data.items.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDate(entry.postingDate)}</td>
                    <td>{entry.book}</td>
                    <td>
                      <Link to={`/journal/${entry.id}`}>{entry.description}</Link>
                    </td>
                    <td>{entry.basis}</td>
                    <td className="num">
                      <Money value={entry.amount} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}
    </>
  )
}
